// app/javascript/controllers/tiptap_controller.js
import { Controller } from "@hotwired/stimulus"
import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Node, Mark, mergeAttributes } from '@tiptap/core'
// alias, ať se to nesrazí s window.Plugin (DOM)
import { Plugin as PMPlugin } from 'prosemirror-state'
import { DrawRule } from '../editor/extensions/draw-rule'
import { Gallery, GalleryImage } from '../editor/extensions/gallery'
import { Attachment } from '../editor/extensions/attachment'

// --- Custom Image extension: size přes data-size + class image-sm|md|lg
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      size: {
        default: 'lg',
        parseHTML: el =>
          el.getAttribute('data-size') ||
          (el.className.match(/\bimage-(sm|md|lg)\b/)?.[1] ?? 'lg'),
        renderHTML: attrs => {
          const s = ['sm','md','lg'].includes(attrs.size) ? attrs.size : 'lg'
          return { 'data-size': s, class: `image-${s}` }
        },
      },
    }
  },
  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: el => {
          // Neparsuj img uvnitř galerie
          if (el.closest('.gallery')) return false
          return {
            src: el.getAttribute('src'),
            alt: el.getAttribute('alt'),
            title: el.getAttribute('title'),
          }
        },
      },
    ]
  },
  addCommands() {
    return {
      ...this.parent?.(),
      setImageSize: size => ({ commands }) => {
        const s = ['sm','md','lg'].includes(size) ? size : 'lg'
        return commands.updateAttributes('image', { size: s })
      },
    }
  },
})

// --- Link, co blokuje klik a pustí jen Ctrl/Cmd
const ModClickLink = Link.extend({
  addOptions() {
    return { ...this.parent?.(), openOnClick: false, onLinkClick: null }
  },
  addProseMirrorPlugins() {
    const onLinkClick = this.options.onLinkClick
    return [
      new PMPlugin({
        props: {
          handleClick(view, _pos, event) {
            const a = event.target?.closest?.('a')
            if (!a) return false
            // Ignoruj odkazy, které jsou přílohy
            if (a.classList.contains('attachment')) return false
            if (!view.editable) return false
            if (event.ctrlKey || event.metaKey) return false
            event.preventDefault()
            event.stopPropagation()
            if (typeof onLinkClick === 'function') onLinkClick(a, event, view)
            return true
          },
        },
      }),
    ]
  },
})

// --- Box
const Box = Node.create({
  name: 'box',
  group: 'block',
  content: 'block+',
  addOptions() { return { HTMLAttributes: {} } },
  parseHTML() { return [{ tag: 'div[class*="box"]' }] },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { class: 'box' }), 0]
  },
  addCommands() {
    return {
      setBox: () => ({ commands }) => commands.wrapIn(this.name),
      toggleBox: () => ({ commands }) => commands.toggleWrap(this.name),
      unsetBox: () => ({ commands }) => commands.lift(this.name),
    }
  },
})

// --- Small paragraph
const SmallParagraph = Node.create({
  name: 'smallParagraph',
  priority: 2000, // ať přepere paragraph ze StarterKitu
  group: 'block',
  content: 'inline*',
  addOptions() { return { HTMLAttributes: {} } },
  parseHTML() {
    return [{
      tag: 'p',
      getAttrs: el => {
        // buďme přesní ať to nespustí na "smallprint" apod.
        return el.classList?.contains('small') ? {} : false
      }
    }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['p', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { class: 'small' }), 0]
  },
  addCommands() {
    return {
      setSmallParagraph: () => ({ commands }) => commands.setNode(this.name),
      toggleSmallParagraph: () => ({ commands }) => commands.toggleNode(this.name, 'paragraph'),
    }
  },
})

// --- Color mark (ponecháno)
const Color = Mark.create({
  name: 'color',
  addOptions() { return { HTMLAttributes: {} } },
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: el => el.getAttribute('class'),
        renderHTML: attrs => attrs.class ? { class: attrs.class } : {},
      },
    }
  },
  parseHTML() {
    return [{ tag: 'span', getAttrs: el => {
      const cn = el.className
      return /color-(black|pink|blue|grey)/.test(cn) ? { class: cn } : false
    }}]
  },
  renderHTML({ HTMLAttributes }) { return ['span', HTMLAttributes, 0] },
  addCommands() {
    return {
      setColor: color => ({ commands }) => commands.setMark(this.name, { class: `color-${color}` }),
      unsetColor: () => ({ commands }) => commands.unsetMark(this.name),
    }
  },
})

export default class extends Controller {
  static targets = ["editor"]
  static values = { content: String, pageId: String }

  connect() {
    // Nastav window.tiptapController hned na začátku
    window.tiptapController = this
    this.initializeEditor()
  }

  initializeEditor() {
    // 1) Nejprve VŽDY vytvoř editor…
    this.editor = new Editor({
      element: this.editorTarget,
      editorProps: {},
      extensions: [
        // dej sem všechny vlastní nody, které kolidují se StarterKitem
        SmallParagraph.configure({ HTMLAttributes: { class: 'small' } }),
        Box.configure({ HTMLAttributes: { class: 'box' } }),
        CustomImage.configure({ HTMLAttributes: { class: 'tiptap-image' } }),
        GalleryImage,
        Gallery,
        Color,
        DrawRule,
        
        // Attachment musí být PŘED ModClickLink, aby se správně parsoval
        Attachment,

        // až potom StarterKit
        StarterKit.configure({
          link: false,
          horizontalRule: false,
        }),

        // ModClickLink až nakonec
        ModClickLink.configure({
          HTMLAttributes: { class: 'tiptap-link', rel: null },
          onLinkClick: (el) => this.editLink(el),
        }),
      ],
      content: this.contentValue || '<p>Začněte psát...</p>',
      onUpdate: () => { 
        this.updateHiddenInput(); 
        this.updateToolbarState() 
      },
      onSelectionUpdate: () => this.updateToolbarState(),
    })

    // …a až POTOM toolbar (jinak můžeš kliknout dřív, než existuje editor)
    this.createToolbar()

    // submit sync - použijeme onbeforeunload místo submit event
    // aby se neinterferovalo s normálním odesláním formuláře
    window.addEventListener('beforeunload', () => {
      if (this.editor) {
        this.updateHiddenInput()
      }
    })

    // listener pro tlačítko + v galerii
    this.editorTarget.addEventListener('gallery:add-images', (e) => {
      console.log('Gallery event received:', e)
      // Guard: editor musí existovat
      if (!this.editor) return
      this.showGalleryDialog()
    })

    // click: otevři edit modál pro IMG/HR/Attachment
    this.editorTarget.addEventListener('click', (e) => {
      const el = e.target
      if (!el?.closest('.ProseMirror')) return
      
      // click na gallery thumb pro výběr
      const fig = el.closest('figure.gallery-thumb')
      if (fig) {
        const pos = this.editor.view.posAtDOM(fig, 0)
        if (typeof pos === 'number') this.editor.chain().focus().setNodeSelection(pos).run()
        return
      }
      
      // click v editoru: klik na IMG uvnitř .gallery → otevři tvůj image modal
      const img = el.closest('img')
      if (img && img.closest('.gallery')) {
        // vyber konkrétní image node
        const pos = this.editor.view.posAtDOM(img, 0)
        if (typeof pos === 'number') this.editor.chain().focus().setNodeSelection(pos).run()
        // reuse tvého showImageEditDialog
        this.showImageEditDialog(img.src, 'lg')
        return
      }
      
      // click na přílohu
      const attachment = el.closest('.attachment')
      if (attachment) {
        e.preventDefault()
        e.stopPropagation()
        
        // Najdi wrapper pro pozici v dokumentu
        const wrapper = attachment.closest('.attachment-wrapper')
        if (wrapper) {
          const pos = this.editor.view.posAtDOM(wrapper, 0)
          if (typeof pos === 'number') {
            this.editor.chain().focus().setNodeSelection(pos).run()
            
            // Získej atributy přílohy z node
            const node = this.editor.state.doc.nodeAt(pos)
            if (node && node.type.name === 'attachment') {
              this.showAttachmentEditDialog(node.attrs)
            }
          }
        }
        return
      }
      
      if (el.tagName === 'IMG') {
        this.editImage(el)
      } else if (el.tagName === 'HR') {
        // vyber node a otevři HR dialog
        const pos = this.editor.view.posAtDOM(el, 0)
        if (typeof pos === 'number') {
          this.editor.chain().focus().setNodeSelection(pos).run()
        }
        this.showHrDialog('edit')
      }
    })

    this.updateToolbarState()
  }

  createToolbar() {
    const toolbar = document.createElement('div')
    toolbar.className = 'tiptap-toolbar'

    // text
    const textFormatting = document.createElement('div')
    textFormatting.className = 'toolbar-group'
    ;[
      { command: 'toggleBold', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"/></svg>', title: 'Tučné' },
      { command: 'toggleItalic', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/></svg>', title: 'Kurzíva' }
    ].forEach(b => {
      const btn = this.createToolbarButton(b)
      textFormatting.appendChild(btn)
    })

    // headings
    const headingGroup = document.createElement('div')
    headingGroup.className = 'toolbar-group'
    const headingSelect = document.createElement('select')
    headingSelect.className = 'toolbar-select'
    headingSelect.innerHTML = `
      <option value="">Odstavec</option>
      <option value="small">Odstavec menší</option>
      <option value="2">Nadpis 2</option>
      <option value="3">Nadpis 3</option>
      <option value="box">Box</option>
    `
    headingSelect.addEventListener('change', () => {
      const v = headingSelect.value
      if (v === 'box') this.editor.chain().focus().toggleBox().run()
      else if (v === 'small') this.editor.chain().focus().toggleSmallParagraph().run()
      else if (v) this.editor.chain().focus().toggleHeading({ level: parseInt(v) }).run()
      else this.editor.chain().focus().setParagraph().run()
    })
    headingGroup.appendChild(headingSelect)

    // lists
    const listsGroup = document.createElement('div')
    listsGroup.className = 'toolbar-group'
    ;[
      { command: 'toggleBulletList', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>', title: 'Seznam' },
      { command: 'toggleOrderedList', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/></svg>', title: 'Číslovaný' }
    ].forEach(b => {
      const btn = this.createToolbarButton(b)
      listsGroup.appendChild(btn)
    })

    // tools: Link, Image, Gallery, HR (jedno tlačítko -> modál)
    const toolsGroup = document.createElement('div')
    toolsGroup.className = 'toolbar-group'
    ;[
      { command: 'setLink', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>', title: 'Odkaz' },
      { command: 'setImage', icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>', title: 'Obrázek' },
    ].forEach(b => {
      const btn = this.createToolbarButton(b)
      toolsGroup.appendChild(btn)
    })

    // Gallery button
    const galleryBtn = document.createElement('button')
    galleryBtn.type = 'button'
    galleryBtn.className = 'tiptap-toolbar-btn'
    galleryBtn.title = 'Přidat galerii'
    galleryBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>'
    galleryBtn.addEventListener('click', () => this.showGalleryDialog())
    toolsGroup.appendChild(galleryBtn)

    // Attachment button
    const attachmentBtn = document.createElement('button')
    attachmentBtn.type = 'button'
    attachmentBtn.className = 'tiptap-toolbar-btn'
    attachmentBtn.title = 'Přidat přílohu'
    attachmentBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>'
    attachmentBtn.addEventListener('click', () => this.showAttachmentDialog())
    toolsGroup.appendChild(attachmentBtn)

    // jediné tlačítko pro čáru -> otevře modál
    const hrBtn = document.createElement('button')
    hrBtn.type = 'button'
    hrBtn.className = 'tiptap-toolbar-btn'
    hrBtn.title = 'Oddělovač'
    hrBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="10" x2="21" y2="10"/><line x1="3" y1="14" x2="21" y2="14" stroke-dasharray="5,5"/></svg>'
    hrBtn.addEventListener('click', () => this.showHrDialog('insert'))
    toolsGroup.appendChild(hrBtn)

    // collapse
    const collapseGroup = document.createElement('div')
    collapseGroup.className = 'toolbar-group'
    const collapseBtn = document.createElement('button')
    collapseBtn.type = 'button'
    collapseBtn.className = 'tiptap-toolbar-btn tiptap-collapse-btn'
    collapseBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>'
    collapseBtn.title = 'Sbalit editor'
    collapseBtn.setAttribute('data-collapsed', 'false')
    collapseBtn.addEventListener('click', () => this.toggleEditorCollapse())
    collapseGroup.appendChild(collapseBtn)

    // mount
    toolbar.appendChild(textFormatting)
    toolbar.appendChild(headingGroup)
    toolbar.appendChild(listsGroup)
    toolbar.appendChild(toolsGroup)
    toolbar.appendChild(collapseGroup)
    this.editorTarget.parentNode.insertBefore(toolbar, this.editorTarget)
  }

  toggleEditorCollapse() {
    const collapseBtn = this.element.querySelector('.tiptap-collapse-btn')
    const isCollapsed = collapseBtn.getAttribute('data-collapsed') === 'true'
    if (isCollapsed) {
      this.editorTarget.style.display = 'block'
      this.editorTarget.style.height = 'auto'
      this.editorTarget.style.minHeight = '200px'
      collapseBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>'
      collapseBtn.title = 'Sbalit editor'
      collapseBtn.setAttribute('data-collapsed', 'false')
    } else {
      this.editorTarget.style.display = 'none'
      collapseBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/><line x1="12" y1="5" x2="12" y2="19"/></svg>'
      collapseBtn.title = 'Rozbalit editor'
      collapseBtn.setAttribute('data-collapsed', 'true')
    }
  }

  createToolbarButton(button) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'tiptap-toolbar-btn'
    btn.innerHTML = button.icon
    btn.title = button.title
    btn.setAttribute('data-command', button.command)
    btn.addEventListener('click', () => {
      if (!this.editor) return
      if (button.command === 'setLink') this.setLink()
      else if (button.command === 'setImage') this.setImage()
      else this.editor.chain().focus()[button.command](button.args).run()
    })
    return btn
  }

  // --- Link dialogs
  setLink() { /* stejné jako máš */ this.showLinkDialog('') }
  editLink(link) {
    const currentUrl = link.getAttribute('href')
    const currentTarget = link.getAttribute('target')
    const isButton = link.classList.contains('btn')
    this.showLinkDialog(currentUrl, currentTarget, isButton)
  }
  showLinkDialog(currentUrl = '', currentTarget = '', isButton = false) {
    const modal = document.createElement('div')
    modal.className = 'tiptap-link-modal'
    modal.innerHTML = `
      <div class="tiptap-link-modal-content">
        <h3>${currentUrl ? 'Upravit odkaz' : 'Přidat odkaz'}</h3>
        <input type="url" class="tiptap-link-input" value="${currentUrl || ''}" placeholder="https://example.com" />
        <div class="tiptap-link-options">
          <label class="tiptap-link-checkbox-label">
            <input type="checkbox" class="tiptap-link-checkbox" ${currentTarget === '_blank' ? 'checked' : ''} />
            Otevírat v novém tabu
          </label>
          <label class="tiptap-link-checkbox-label">
            <input type="checkbox" class="tiptap-button-checkbox" ${isButton ? 'checked' : ''} />
            Vytvořit jako tlačítko
          </label>
        </div>
        <div class="tiptap-link-buttons">
          <button type="button" class="tiptap-link-cancel">Zrušit</button>
          <button type="button" class="tiptap-link-remove" ${currentUrl ? '' : 'style="display:none"'}>Odebrat</button>
          <button type="button" class="tiptap-link-save">Uložit</button>
        </div>
      </div>`
    document.body.appendChild(modal)
    const input = modal.querySelector('.tiptap-link-input')
    const checkbox = modal.querySelector('.tiptap-link-checkbox')
    const buttonCheckbox = modal.querySelector('.tiptap-button-checkbox')
    const cancelBtn = modal.querySelector('.tiptap-link-cancel')
    const removeBtn = modal.querySelector('.tiptap-link-remove')
    const saveBtn = modal.querySelector('.tiptap-link-save')
    const cleanup = () => document.body.removeChild(modal)
    cancelBtn.addEventListener('click', cleanup)
    removeBtn?.addEventListener('click', () => { this.editor.chain().focus().unsetLink().run(); cleanup() })
    saveBtn.addEventListener('click', () => {
      const url = input.value.trim()
      if (url) {
        const attrs = { href: url }
        if (checkbox.checked) attrs.target = '_blank'
        if (buttonCheckbox.checked) attrs.class = 'btn btn-lg btn-black'
        this.editor.chain().focus().setLink(attrs).run()
      }
      cleanup()
    })
    input.addEventListener('keydown', e => { if (e.key === 'Enter') saveBtn.click(); if (e.key === 'Escape') cleanup() })
    modal.addEventListener('click', e => { if (e.target === modal) cleanup() })
    input.focus(); input.select()
  }

  // --- Obrázek: vložení + edit přes modál (toolbar pro velikost jsme zrušili)
  setImage() { this.showImageDialog('') }

  editImage(imgEl) {
    const pos = this.editor.view.posAtDOM(imgEl, 0)
    if (typeof pos === 'number') this.editor.chain().focus().setNodeSelection(pos).run()
    const currentSize =
      imgEl.getAttribute('data-size') ||
      (imgEl.className.match(/\bimage-(sm|md|lg)\b/)?.[1] ?? 'lg')
    this.showImageEditDialog(imgEl.src, currentSize)
  }

  showImageDialog(currentUrl = '') {
    const modal = document.createElement('div')
    modal.className = 'tiptap-link-modal'
    modal.innerHTML = `
      <div class="tiptap-link-modal-content">
        <h3>${currentUrl ? 'Upravit obrázek' : 'Přidat obrázek'}</h3>
        <ul class="nav nav-tabs" id="imageTabs" role="tablist">
          <li class="nav-item"><button class="nav-link active" id="file-tab" data-bs-toggle="tab" data-bs-target="#file-pane" type="button">Ze souboru</button></li>
          <li class="nav-item"><button class="nav-link" id="url-tab" data-bs-toggle="tab" data-bs-target="#url-pane" type="button">Z URL</button></li>
        </ul>
        <div class="tab-content" id="imageTabContent">
          <div class="tab-pane fade show active" id="file-pane"><input type="file" class="tiptap-file-input" accept="image/*" /><div class="file-info" style="display:none;"><span class="file-name"></span><span class="file-size"></span></div></div>
          <div class="tab-pane fade" id="url-pane"><input type="url" class="tiptap-link-input" value="${currentUrl}" placeholder="https://example.com/image.jpg" /></div>
        </div>
        <div class="upload-progress" style="display:none;"><div class="progress-bar"><div class="progress-fill"></div></div><span class="progress-text">Nahrávám...</span></div>
        <div class="tiptap-image-options">
          <label class="tiptap-image-size-label">Velikost obrázku:
            <select class="tiptap-image-size-select">
              <option value="sm">Malý (1/3)</option>
              <option value="md">Střední (1/2)</option>
              <option value="lg" selected>Velký (100%)</option>
            </select>
          </label>
        </div>
        <div class="tiptap-link-buttons">
          <button type="button" class="tiptap-link-cancel">Zrušit</button>
          <button type="button" class="tiptap-link-save" disabled>Uložit</button>
        </div>
      </div>`
    document.body.appendChild(modal)

    const fileInput = modal.querySelector('.tiptap-file-input')
    const urlInput = modal.querySelector('.tiptap-link-input')
    const cancelBtn = modal.querySelector('.tiptap-link-cancel')
    const saveBtn = modal.querySelector('.tiptap-link-save')
    const fileInfo = modal.querySelector('.file-info')
    const fileName = modal.querySelector('.file-name')
    const fileSize = modal.querySelector('.file-size')
    const uploadProgress = modal.querySelector('.upload-progress')
    const progressFill = modal.querySelector('.progress-fill')
    const progressText = modal.querySelector('.progress-text')
    const imageSizeSelect = modal.querySelector('.tiptap-image-size-select')

    let selectedFile = null
    const updateSave = () => {
      const activeTab = modal.querySelector('.nav-link.active')
      const isFile = activeTab?.id === 'file-tab'
      const isUrl = activeTab?.id === 'url-tab'
      saveBtn.disabled = !((isFile && selectedFile) || (isUrl && urlInput.value.trim()))
    }

    modal.addEventListener('shown.bs.tab', updateSave)
    fileInput.addEventListener('change', e => {
      selectedFile = e.target.files[0] || null
      if (selectedFile) {
        // Kontrola velikosti souboru (5MB)
        const MAX_FILE_SIZE = 5 * 1024 * 1024
        if (selectedFile.size > MAX_FILE_SIZE) {
          alert(`Soubor je příliš velký (max 5MB): ${selectedFile.name}`)
          selectedFile = null
          fileInput.value = ''
          fileInfo.style.display = 'none'
          updateSave()
          return
        }
        fileName.textContent = selectedFile.name
        fileSize.textContent = this.formatFileSize(selectedFile.size)
        fileInfo.style.display = 'block'
      } else fileInfo.style.display = 'none'
      updateSave()
    })
    urlInput?.addEventListener('input', updateSave)

    const cleanup = () => document.body.removeChild(modal)
    cancelBtn.addEventListener('click', cleanup)

    saveBtn.addEventListener('click', async () => {
      const activeTab = modal.querySelector('.nav-link.active')
      const isFile = activeTab?.id === 'file-tab'
      const isUrl = activeTab?.id === 'url-tab'
      const size = imageSizeSelect.value || 'lg'
      if (isFile && selectedFile) {
        await this.uploadImage(selectedFile, progressFill, progressText, uploadProgress, size)
      } else if (isUrl && urlInput.value.trim()) {
        const url = urlInput.value.trim()
        this.editor.chain().focus().setImage({ src: url, size }).run()
      }
      cleanup()
    })

    const handleKeydown = e => { if (e.key === 'Enter' && !saveBtn.disabled) saveBtn.click(); if (e.key === 'Escape') cleanup() }
    fileInput.addEventListener('keydown', handleKeydown)
    urlInput?.addEventListener('keydown', handleKeydown)
    modal.addEventListener('click', e => { if (e.target === modal) cleanup() })
    fileInput.focus()
  }

  showImageEditDialog(currentSrc, currentSize = 'lg') {
    const modal = document.createElement('div')
    modal.className = 'tiptap-link-modal'
    modal.innerHTML = `
      <div class="tiptap-link-modal-content">
        <h3>Upravit obrázek</h3>
        <div class="tiptap-image-preview">
          <img src="${currentSrc}" alt="Náhled" style="max-width:200px;max-height:200px;border-radius:.5rem;margin-bottom:1rem;" />
        </div>
        <div class="tiptap-image-options">
          <label class="tiptap-image-size-label">Velikost obrázku:
            <select class="tiptap-image-size-select">
              <option value="sm" ${currentSize === 'sm' ? 'selected' : ''}>Malý (1/3)</option>
              <option value="md" ${currentSize === 'md' ? 'selected' : ''}>Střední (1/2)</option>
              <option value="lg" ${currentSize === 'lg' ? 'selected' : ''}>Velký (100%)</option>
            </select>
          </label>
        </div>
        <div class="tiptap-link-buttons">
          <button type="button" class="tiptap-link-cancel">Zrušit</button>
          <button type="button" class="tiptap-link-remove">Odebrat obrázek</button>
          <button type="button" class="tiptap-link-save">Uložit změny</button>
        </div>
      </div>`
    document.body.appendChild(modal)

    const cancelBtn = modal.querySelector('.tiptap-link-cancel')
    const removeBtn = modal.querySelector('.tiptap-link-remove')
    const saveBtn = modal.querySelector('.tiptap-link-save')
    const sizeSel = modal.querySelector('.tiptap-image-size-select')

    const cleanup = () => document.body.removeChild(modal)
    cancelBtn.addEventListener('click', cleanup)
    removeBtn.addEventListener('click', () => { this.editor.chain().focus().deleteSelection().run(); cleanup() })
    saveBtn.addEventListener('click', () => {
      const newSize = sizeSel.value || 'lg'
      const ok = this.editor.chain().focus().setImageSize(newSize).run()
      if (!ok) {
        const { state } = this.editor
        const { $from } = state.selection
        const node = $from.nodeAfter || $from.nodeBefore
        if (node?.type?.name === 'image') {
          const pos = $from.pos + (node === $from.nodeBefore ? -node.nodeSize : 0)
          this.editor.chain().focus().setNodeSelection(pos).setImageSize(newSize).run()
        }
      }
      cleanup()
    })
    sizeSel.addEventListener('keydown', e => { if (e.key === 'Enter') saveBtn.click(); if (e.key === 'Escape') cleanup() })
    modal.addEventListener('click', e => { if (e.target === modal) cleanup() })
    sizeSel.focus()
  }

   // --- HR: jediné tlačítko → modál (typ + barva), funguje i pro edit vybrané čáry
   showHrDialog(mode = 'insert') {
     // přečti aktuální stav, pokud je vybraná čára
     let dashed = false
     let color = ''
     const selNode = this.editor.state.selection?.node
     if (selNode && selNode.type?.name === 'drawRule') {
       dashed = !!selNode.attrs.dashed
       color = selNode.attrs.color || ''
     }

     const modal = document.createElement('div')
     modal.className = 'tiptap-link-modal'
     modal.innerHTML = `
       <div class="tiptap-link-modal-content">
         <h3>${selNode && selNode.type?.name === 'drawRule' ? 'Upravit oddělovač' : 'Vložit oddělovač'}</h3>
         
         <div class="tiptap-hr-preview mb-4">
           <div class="text-center">
             <div class="hr-preview-container" style="margin: 1.5rem 0; padding: 1.5rem; border: 2px solid #e5e7eb; border-radius: 0.75rem; background: #ffffff; box-shadow: 0 2px 4px rgba(0,0,0,0.05);">
               <div class="hr-sample" style="height: 0; border-top: 3px ${dashed ? 'dashed' : 'solid'} ${color === 'pink' ? 'var(--pink)' : color === 'blue' ? 'var(--blue)' : color === 'grey' ? 'var(--grey)' : '#000000'}; margin: 0.5rem 0;"></div>
             </div>
             <small class="text-muted fw-medium">Náhled oddělovače</small>
           </div>
         </div>
         
         <div class="tiptap-hr-options">
           <div class="mb-3">
             <label class="form-label fw-medium">Typ čáry:</label>
             <select class="form-select tiptap-hr-type">
               <option value="solid" ${!dashed ? 'selected' : ''}>Plná čára</option>
               <option value="dashed" ${dashed ? 'selected' : ''}>Čárkovaná čára</option>
             </select>
           </div>
           
           <div class="mb-3">
             <label class="form-label fw-medium">Barva:</label>
             <select class="form-select tiptap-hr-color">
               <option value="" ${color === '' ? 'selected' : ''}>Černá (výchozí)</option>
               <option value="black" ${color === 'black' ? 'selected' : ''}>Černá</option>
               <option value="grey" ${color === 'grey' ? 'selected' : ''}>Šedá</option>
               <option value="pink" ${color === 'pink' ? 'selected' : ''}>Růžová</option>
               <option value="blue" ${color === 'blue' ? 'selected' : ''}>Modrá</option>
             </select>
           </div>
         </div>
         
         <div class="tiptap-link-buttons">
           <button type="button" class="tiptap-link-cancel">Zrušit</button>
           ${selNode && selNode.type?.name === 'drawRule' ? '<button type="button" class="tiptap-hr-remove">Odebrat</button>' : ''}
           <button type="button" class="tiptap-link-save">Uložit</button>
         </div>
       </div>`
    document.body.appendChild(modal)

     const cancelBtn = modal.querySelector('.tiptap-link-cancel')
     const saveBtn = modal.querySelector('.tiptap-link-save')
     const removeBtn = modal.querySelector('.tiptap-hr-remove')
     const typeSel = modal.querySelector('.tiptap-hr-type')
     const colorSel = modal.querySelector('.tiptap-hr-color')
     const hrSample = modal.querySelector('.hr-sample')

     // Update preview when settings change
     const updatePreview = () => {
       const isDashed = typeSel.value === 'dashed'
       const selectedColor = colorSel.value
       const borderColor = selectedColor === 'pink' ? 'var(--pink)' : 
                          selectedColor === 'blue' ? 'var(--blue)' : 
                          selectedColor === 'grey' ? 'var(--grey)' : '#000000'
       
       hrSample.style.borderTop = `3px ${isDashed ? 'dashed' : 'solid'} ${borderColor}`
     }

     typeSel.addEventListener('change', updatePreview)
     colorSel.addEventListener('change', updatePreview)

     const cleanup = () => document.body.removeChild(modal)
     cancelBtn.addEventListener('click', cleanup)
     removeBtn?.addEventListener('click', () => { this.editor.chain().focus().deleteSelection().run(); cleanup() })

     saveBtn.addEventListener('click', () => {
       const dashedNew = typeSel.value === 'dashed'
       const colorNew = ['black','grey','pink','blue'].includes(colorSel.value) ? colorSel.value : null
       const node = this.editor.state.selection?.node
       if (node && node.type?.name === 'drawRule') {
         // update vybrané čáry
         this.editor.chain().focus().updateAttributes('drawRule', { dashed: dashedNew, color: colorNew }).run()
       } else {
         // vložit novou čáru
         this.editor.chain().focus().insertDrawRule({ dashed: dashedNew, color: colorNew }).run()
       }
       cleanup()
     })

     const handleKeydown = e => { if (e.key === 'Enter') saveBtn.click(); if (e.key === 'Escape') cleanup() }
     typeSel.addEventListener('keydown', handleKeydown)
     colorSel.addEventListener('keydown', handleKeydown)
     modal.addEventListener('click', e => { if (e.target === modal) cleanup() })
     typeSel.focus()
  }

  updateToolbarState() {
    if (!this.editor) return
    // basic states
    const boldBtn = this.element.querySelector('[data-command="toggleBold"]')
    const italicBtn = this.element.querySelector('[data-command="toggleItalic"]')
    const bulletListBtn = this.element.querySelector('[data-command="toggleBulletList"]')
    const orderedListBtn = this.element.querySelector('[data-command="toggleOrderedList"]')
    const linkBtn = this.element.querySelector('[data-command="setLink"]')

    boldBtn?.classList.toggle('is-active', this.editor.isActive('bold'))
    italicBtn?.classList.toggle('is-active', this.editor.isActive('italic'))
    bulletListBtn?.classList.toggle('is-active', this.editor.isActive('bulletList'))
    orderedListBtn?.classList.toggle('is-active', this.editor.isActive('orderedList'))
    linkBtn?.classList.toggle('is-active', this.editor.isActive('link'))

    // headings select
    const headingSelect = this.element.querySelector('.toolbar-select')
    if (headingSelect) {
      if (this.editor.isActive('heading', { level: 1 })) headingSelect.value = '1'
      else if (this.editor.isActive('heading', { level: 2 })) headingSelect.value = '2'
      else if (this.editor.isActive('heading', { level: 3 })) headingSelect.value = '3'
      else if (this.editor.isActive('box')) headingSelect.value = 'box'
      else if (this.editor.isActive('smallParagraph')) headingSelect.value = 'small'
      else headingSelect.value = ''
    }
  }

  updateHiddenInput() {
    const hiddenInput = this.element.previousElementSibling
    if (hiddenInput && hiddenInput.type === 'hidden') {
      hiddenInput.value = this.editor.getHTML()
    }
  }

  async uploadImage(file, progressFill, progressText, uploadProgress, size = 'lg') {
    try {
      if (uploadProgress && uploadProgress.style) uploadProgress.style.display = 'block'
      if (progressText && progressText.textContent !== undefined) progressText.textContent = 'Nahrávám...'
      if (progressFill && progressFill.style) progressFill.style.width = '0%'
      const pageId = this.getPageId()
      const formData = new FormData()
      formData.append('image', file)
      const uploadUrl = pageId ? `/admin/pages/${pageId}/images` : '/admin/images'
      const xhr = new XMLHttpRequest()
      return new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', e => {
          if (e.lengthComputable && progressFill && progressFill.style && progressText) {
            const pct = (e.loaded / e.total) * 100
            progressFill.style.width = `${pct}%`
            progressText.textContent = `Nahrávám... ${Math.round(pct)}%`
          }
        })
        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText)
            if (response.success) {
              if (uploadProgress && uploadProgress.style) uploadProgress.style.display = 'none'
              // Pouze pokud nejsou všechny parametry null (tedy není to volání z galerie)
              if (progressFill !== null || progressText !== null || uploadProgress !== null) {
                this.editor.chain().focus().setImage({ src: response.url, size }).run()
              }
              resolve(response)
            } else reject(new Error(response.error || 'Upload failed'))
          } else reject(new Error(`Upload failed: ${xhr.status}`))
        })
        xhr.addEventListener('error', () => reject(new Error('Network error during upload')))
        xhr.open('POST', uploadUrl)
        xhr.setRequestHeader('X-CSRF-Token', document.querySelector('meta[name="csrf-token"]').getAttribute('content'))
        xhr.send(formData)
      })
    } catch (err) {
      console.error('Upload error:', err)
      alert(`Chyba při nahrávání obrázku: ${err.message}`)
    } finally {
      if (uploadProgress && uploadProgress.style) {
        uploadProgress.style.display = 'none'
      }
    }
  }

  getPageId() {
    if (this.pageIdValue && this.pageIdValue !== 'new') return this.pageIdValue
    const urlMatch = window.location.pathname.match(/\/admin\/pages\/(\d+)/)
    if (urlMatch) return urlMatch[1]
    const form = this.element.closest('form')
    const actionMatch = form?.action?.match(/\/admin\/pages\/(\d+)/)
    return actionMatch ? actionMatch[1] : null
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 B'
    const k = 1024, sizes = ['B','KB','MB','GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  async uploadSingle(file) {
    const pageId = this.getPageId()
    const formData = new FormData()
    formData.append('image', file)
    const uploadUrl = pageId ? `/admin/pages/${pageId}/images` : '/admin/images'
    const xhr = new XMLHttpRequest()
    return new Promise((resolve, reject) => {
      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText)
          if (response.success) {
            resolve(response)
          } else reject(new Error(response.error || 'Upload failed'))
        } else reject(new Error(`Upload failed: ${xhr.status}`))
      })
      xhr.addEventListener('error', () => reject(new Error('Network error during upload')))
      xhr.open('POST', uploadUrl)
      xhr.setRequestHeader('X-CSRF-Token', document.querySelector('meta[name="csrf-token"]').getAttribute('content'))
      xhr.send(formData)
    })
  }

  async uploadMany(files) {
    // vrať [{src}, ...] s finálními URL; použij tvůj existující /admin/images endpoint
    const out = []
    for (const f of files) {
      const res = await this.uploadSingle(f)   // vytvoř si uploadSingle podobně jako uploadImage
      out.push({ src: res.url })
    }
    return out
  }

  convertSelectionToGallery() {
    const { state } = this.editor
    const { from, to } = state.selection
    const images = []
    state.doc.nodesBetween(from, to, (node) => {
      if (node.type?.name === 'image') images.push({ src: node.attrs.src })
    })
    if (images.length === 0) return
    // smaž původní obsah a vlož galerii
    this.editor.chain().focus().deleteSelection().insertGallery(images).run()
  }

  showGalleryDialog(galleryPos = null) {
    // 2) Guard hned na začátku modálu, ať to nespadne v save handleru
    if (!this.editor) {
      alert('Editor ještě není připraven. Zkuste to znovu za chvíli.')
      return
    }
    const modal = document.createElement('div')
    modal.className = 'tiptap-link-modal'
    modal.innerHTML = `
      <div class="tiptap-link-modal-content" style="max-width:720px;">
        <h3>${galleryPos ? 'Přidat do galerie' : 'Vložit galerii'}</h3>
        <input type="file" class="tiptap-gallery-input" accept="image/*" multiple />
        <div class="tiptap-gallery-preview" style="display:flex;flex-wrap:wrap;gap:8px;margin-top:8px;"></div>
        <div class="tiptap-link-buttons" style="margin-top:12px;">
          <button type="button" class="tiptap-link-cancel">Zrušit</button>
          <button type="button" class="tiptap-link-save" disabled>Uložit</button>
        </div>
      </div>`
    document.body.appendChild(modal)
    const input   = modal.querySelector('.tiptap-gallery-input')
    const preview = modal.querySelector('.tiptap-gallery-preview')
    const cancel  = modal.querySelector('.tiptap-link-cancel')
    const save    = modal.querySelector('.tiptap-link-save')
    const cleanup = () => document.body.removeChild(modal)
    const files = []  // File[]
    input.addEventListener('change', (e) => {
      preview.innerHTML = ''
      files.length = 0
      const selected = Array.from(e.target.files || [])
      selected.forEach(f => {
        files.push(f)
        const img = document.createElement('img')
        img.src = URL.createObjectURL(f) // jen preview
        img.style.cssText = 'display:block;width:100px;height:auto;border:1px solid #ddd;border-radius:4px;'
        preview.appendChild(img)
      })
      save.disabled = files.length === 0
    })
    cancel.addEventListener('click', cleanup)
    save.addEventListener('click', async () => {
      if (!this.editor) {
        alert('Editor se právě inicializuje, uložení není dostupné.')
        return
      }
      try {
        const uploaded = await this.uploadMany(files)  // [{src}]
        // Pokud máme pozici galerie, přidej do té konkrétní galerie
        if (galleryPos !== null) {
          console.log('Adding to gallery at position:', galleryPos)
          // Nastav selection na galerii a pak použij appendToGallery
          this.editor.chain().focus().setNodeSelection(galleryPos).appendToGallery(uploaded).run()
        } else {
          // Zkus nejdřív přidat do existující galerie, pokud to selže, vytvoř novou
          const success = this.editor.chain().focus().appendToGallery(uploaded).run()
          if (!success) {
            this.editor.chain().focus().insertGallery(uploaded).run()
          }
        }
        cleanup()
      } catch (e) {
        console.error('Gallery upload failed', e)
        console.error('Error details:', e.message, e.stack)
        alert(`Chyba při nahrávání obrázků do galerie: ${e.message}`)
      }
    })
    modal.addEventListener('click', e => { if (e.target === modal) cleanup() })
  }

  showAttachmentDialog() {
    if (!this.editor) {
      alert('Editor ještě není připraven. Zkuste to znovu za chvíli.')
      return
    }

    const modal = document.createElement('div')
    modal.className = 'tiptap-link-modal'
    modal.innerHTML = `
      <div class="tiptap-link-modal-content">
        <h3>Přidat přílohu</h3>
        <div style="margin-bottom: 12px;">
          <label style="display:block; font-weight:500; margin-bottom:6px;">Název přílohy:</label>
          <input type="text" class="tiptap-attachment-title" placeholder="Např.: Formulář přihlášky" style="width:100%; padding:8px; border:1px solid #d1d5db; border-radius:6px;" />
        </div>
        <div style="margin-bottom: 12px;">
          <label style="display:block; font-weight:500; margin-bottom:6px;">Soubor:</label>
          <input type="file" class="tiptap-attachment-input" />
        </div>
        <div class="file-info" style="display:none; margin-top: 12px; padding: 12px; background: #f3f4f6; border-radius: 8px;">
          <div style="display:flex; align-items:center; gap:8px;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
              <polyline points="13 2 13 9 20 9"/>
            </svg>
            <div style="flex:1;">
              <div class="file-name" style="font-weight:500;"></div>
              <div class="file-size" style="font-size:0.875rem;color:#6b7280;"></div>
            </div>
          </div>
        </div>
        <div class="upload-progress" style="display:none; margin-top: 12px;">
          <div class="progress-bar" style="background:#e5e7eb;height:8px;border-radius:4px;overflow:hidden;">
            <div class="progress-fill" style="background:#3b82f6;height:100%;width:0%;transition:width 0.3s;"></div>
          </div>
          <span class="progress-text" style="display:block;margin-top:4px;font-size:0.875rem;color:#6b7280;">Nahrávám...</span>
        </div>
        <div class="tiptap-link-buttons" style="margin-top: 16px;">
          <button type="button" class="tiptap-link-cancel">Zrušit</button>
          <button type="button" class="tiptap-link-save" disabled>Nahrát</button>
        </div>
      </div>`
    document.body.appendChild(modal)

    const titleInput = modal.querySelector('.tiptap-attachment-title')
    const fileInput = modal.querySelector('.tiptap-attachment-input')
    const cancelBtn = modal.querySelector('.tiptap-link-cancel')
    const saveBtn = modal.querySelector('.tiptap-link-save')
    const fileInfo = modal.querySelector('.file-info')
    const fileName = modal.querySelector('.file-name')
    const fileSize = modal.querySelector('.file-size')
    const uploadProgress = modal.querySelector('.upload-progress')
    const progressFill = modal.querySelector('.progress-fill')
    const progressText = modal.querySelector('.progress-text')

    let selectedFile = null

    fileInput.addEventListener('change', e => {
      selectedFile = e.target.files[0] || null
      if (selectedFile) {
        // Kontrola velikosti souboru (10MB)
        const MAX_FILE_SIZE = 10 * 1024 * 1024
        if (selectedFile.size > MAX_FILE_SIZE) {
          alert(`Soubor je příliš velký (max 10MB): ${selectedFile.name}`)
          selectedFile = null
          fileInput.value = ''
          fileInfo.style.display = 'none'
          saveBtn.disabled = true
          return
        }
        fileName.textContent = selectedFile.name
        fileSize.textContent = this.formatFileSize(selectedFile.size)
        fileInfo.style.display = 'block'
        
        // Pokud není zadán název, použij název souboru bez přípony
        if (!titleInput.value.trim()) {
          const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '')
          titleInput.value = nameWithoutExt
        }
        
        saveBtn.disabled = false
      } else {
        fileInfo.style.display = 'none'
        saveBtn.disabled = true
      }
    })

    const cleanup = () => document.body.removeChild(modal)
    cancelBtn.addEventListener('click', cleanup)

    saveBtn.addEventListener('click', async () => {
      if (!selectedFile) return

      try {
        uploadProgress.style.display = 'block'
        progressFill.style.width = '0%'
        progressText.textContent = 'Nahrávám...'

        const response = await this.uploadAttachment(selectedFile, progressFill, progressText)
        
        if (response.success) {
          this.editor.chain().focus().insertAttachment({
            href: response.url,
            title: titleInput.value.trim() || response.filename,
            filename: response.filename,
            size: response.size,
            contentType: response.content_type
          }).run()
          cleanup()
        } else {
          alert(`Chyba při nahrávání: ${response.error}`)
        }
      } catch (e) {
        console.error('Attachment upload failed', e)
        alert(`Chyba při nahrávání přílohy: ${e.message}`)
      } finally {
        uploadProgress.style.display = 'none'
      }
    })

    modal.addEventListener('click', e => { if (e.target === modal) cleanup() })
    titleInput.focus()
  }

  showAttachmentEditDialog(currentAttrs) {
    if (!this.editor) {
      alert('Editor ještě není připraven.')
      return
    }

    const modal = document.createElement('div')
    modal.className = 'tiptap-link-modal'
    modal.innerHTML = `
      <div class="tiptap-link-modal-content">
        <h3>Upravit přílohu</h3>
        <div style="margin-bottom: 12px;">
          <label style="display:block; font-weight:500; margin-bottom:6px;">Název přílohy:</label>
          <input type="text" class="tiptap-attachment-title" value="${currentAttrs.title || currentAttrs.filename || ''}" style="width:100%; padding:8px; border:1px solid #d1d5db; border-radius:6px;" />
        </div>
        <div class="file-info" style="padding: 12px; background: #f3f4f6; border-radius: 8px;">
          <div style="display:flex; align-items:center; gap:8px;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
              <polyline points="13 2 13 9 20 9"/>
            </svg>
            <div style="flex:1;">
              <div class="file-name" style="font-weight:500;">${currentAttrs.filename || 'Příloha'}</div>
              <div class="file-size" style="font-size:0.875rem;color:#6b7280;">${currentAttrs.size ? this.formatFileSize(currentAttrs.size) : ''}</div>
            </div>
          </div>
        </div>
        <div class="tiptap-link-buttons" style="margin-top: 16px;">
          <button type="button" class="tiptap-link-cancel">Zrušit</button>
          <button type="button" class="tiptap-link-remove">Odebrat přílohu</button>
          <button type="button" class="tiptap-link-save">Uložit</button>
        </div>
      </div>`
    document.body.appendChild(modal)

    const titleInput = modal.querySelector('.tiptap-attachment-title')
    const cancelBtn = modal.querySelector('.tiptap-link-cancel')
    const removeBtn = modal.querySelector('.tiptap-link-remove')
    const saveBtn = modal.querySelector('.tiptap-link-save')

    const cleanup = () => document.body.removeChild(modal)
    cancelBtn.addEventListener('click', cleanup)
    
    removeBtn.addEventListener('click', () => {
      if (confirm('Opravdu chcete odebrat tuto přílohu?')) {
        this.editor.chain().focus().deleteSelection().run()
        cleanup()
      }
    })

    saveBtn.addEventListener('click', () => {
      const newTitle = titleInput.value.trim()
      if (newTitle) {
        this.editor.chain().focus().updateAttributes('attachment', { 
          title: newTitle 
        }).run()
      }
      cleanup()
    })

    titleInput.addEventListener('keydown', e => { 
      if (e.key === 'Enter') saveBtn.click()
      if (e.key === 'Escape') cleanup()
    })
    
    modal.addEventListener('click', e => { if (e.target === modal) cleanup() })
    titleInput.focus()
    titleInput.select()
  }

  async uploadAttachment(file, progressFill, progressText) {
    const pageId = this.getPageId()
    const formData = new FormData()
    formData.append('attachment', file)
    const uploadUrl = pageId ? `/admin/pages/${pageId}/attachments` : '/admin/attachments'
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      
      xhr.upload.addEventListener('progress', e => {
        if (e.lengthComputable && progressFill && progressText) {
          const pct = (e.loaded / e.total) * 100
          progressFill.style.width = `${pct}%`
          progressText.textContent = `Nahrávám... ${Math.round(pct)}%`
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText)
          resolve(response)
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`))
        }
      })

      xhr.addEventListener('error', () => reject(new Error('Network error during upload')))
      
      xhr.open('POST', uploadUrl)
      xhr.setRequestHeader('X-CSRF-Token', document.querySelector('meta[name="csrf-token"]').getAttribute('content'))
      xhr.send(formData)
    })
  }

  disconnect() {
    this.editor?.destroy()
    this.editor = null
  }
}